// Centre de contrôle conformité — règle de calcul UNIQUE de l'avancement
// GI/LAB d'un dossier client. Copie conforme de
// backend-myalfa/utils/conformite.js (tableau de bord) : toute
// modification doit être faite dans les deux fichiers.
//
// 4 rubriques : Fiche client (12 champs), Documentation réglementaire
// (5 documents obligatoires), KYC/LAB (5 étapes), Diligences
// administratives (les 7 tâches d'entrée en mission, toujours comptées : une
// tâche absente du dossier compte comme « à faire »).
// Statut : « ok » si tout est complet ; « alerte » si le KYC n'est pas
// validé (ou à réexaminer) ; « encours » sinon.
// « Validation & rapport » n'est acquise que si les 4 étapes précédentes du
// KYC sont complètes : une validation antérieure (ex. signée avant l'ajout
// de nouvelles questions) reste en base mais ne compte plus tant que le
// dossier n'est pas complet.

export const FICHE = [
  ["siren", "SIREN"],
  ["siret", "SIRET"],
  ["formeSociale", "Forme sociale"],
  ["activite", "Activité"],
  ["codeNAF", "Code activité (NAF)"],
  ["paysJuridiction", "Pays (siège)"],
  ["regimeFiscal", "Régime fiscal"],
  ["regimeTVA", "TVA"],
  ["adresse", "Adresse"],
  ["dateCloture", "Date de clôture"],
  ["nomClient", "Dirigeant"],
  ["telephone", "Téléphone"],
];
export const DOCS = [
  ["extraitKbis", "Extrait K-bis / RNE"],
  ["sirene", "Avis de situation SIRENE"],
  ["lettreMission", "Lettre(s) de mission"],
  ["mementoFiscal", "Mémento fiscal"],
  ["rib", "RIB"],
];
const TACHES = ["Preparation", "Diligences", "Verification", "dext", "Mandat_impots", "Urssaf", "EBICS"];
// Titres des 7 tâches par défaut (création d'un client, Portefeuille/Create.js).
const TACHES_TITRES = {
  Preparation: "DPA-0",
  Diligences: "Diligences NpMQ & LAB",
  Verification: "Vérification des premiers obligations fiscales",
  dext: "Paramétrage comptabilité/dext",
  Mandat_impots: "Mandat impôts.gouv.fr",
  Urssaf: "Urssaf.fr",
  EBICS: "Mise en place EBICS",
};
const TACHE_FAITE = ["TERMINE", "Terminé", "Terminer", "DONE"];
const CONN = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q7b", "q8", "q9"];
const ACCE = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8"];

const rempli = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
const estValide = (d) => d?.statut === "valide" || (!d?.statut && !!d?.validatedAt);

function etapesKyc(client) {
  const d = client.kycDossier || null;
  const annuaires = client.annuaires || [];
  const personnesOk =
    annuaires.length > 0 &&
    annuaires.every((a) => {
      const lien = (a.kycPersonnes || []).find((k) => k.clientId === client.id) || {};
      return (lien.roles || []).length > 0 && !!lien.rencontre && !!a.question2Reponse && !!a.question3Reponse;
    });
  const nConn = CONN.filter((q) => d && d[`conn_${q}_reponse`]).length;
  const nAcce = ACCE.filter((q) => d && d[`acce_${q}_reponse`]).length;
  const synth = d?.risqueSynthese || null;
  const lv = { faible: "Faible", moyen: "Moyen", eleve: "Élevé" };
  const risquesOk = !!synth?.retained && !synth?.provisional;
  const precedentesOk = personnesOk && nConn === CONN.length && nAcce === ACCE.length && risquesOk;
  const signe = estValide(d);
  return [
    { id: "associes", label: "Associés / BE", on: personnesOk, info: annuaires.length ? `${annuaires.length} personne${annuaires.length > 1 ? "s" : ""}` : "Aucune" },
    { id: "connaissance", label: "Connaissance client", on: nConn === CONN.length, info: `${nConn}/${CONN.length}` },
    { id: "acceptation", label: "Acceptation mission", on: nAcce === ACCE.length, info: `${nAcce}/${ACCE.length}` },
    {
      id: "risques",
      label: "Risques & vigilance",
      on: risquesOk,
      info: synth?.retained ? `Retenu ${lv[synth.retained]}` : synth?.proposed ? `${lv[synth.proposed]}${synth.provisional ? " (provisoire)" : ""}` : "Non évalué",
    },
    {
      id: "validation",
      label: "Validation & rapport",
      on: signe && precedentesOk,
      info:
        d?.statut === "a_reexaminer"
          ? "À réexaminer"
          : !precedentesOk
          ? signe
            ? "Signé · étapes à compléter"
            : "Étapes à compléter"
          : signe
          ? "Signé"
          : "À signer",
    },
  ];
}

export function computeConformite(client) {
  const fiche = FICHE.map(([k, label]) => ({ k, label, ok: rempli(client[k]) }));
  const lab = client.ficheKycLabs?.[0] || null;
  const docs = DOCS.map(([k, label]) => ({ k, label, ok: !!lab?.[k] }));
  const etapes = etapesKyc(client);
  // Une tâche par type (la plus ancienne, comme l'affichage des tâches
  // internes) ; un type sans tâche compte comme « à faire ».
  const parType = {};
  (client.todos || [])
    .filter((t) => TACHES.includes(t.type))
    .forEach((t) => {
      if (!parType[t.type] || t.id < parType[t.type].id) parType[t.type] = t;
    });
  const taches = TACHES.map((type) => {
    const t = parType[type];
    return { k: t ? t.id : type, label: (t && t.title) || TACHES_TITRES[type], ok: !!t && TACHE_FAITE.includes(t.status) };
  });

  const sections = [
    { id: "fiche", label: "Fiche client", total: fiche.length, done: fiche.filter((x) => x.ok).length, manquants: fiche.filter((x) => !x.ok) },
    { id: "docs", label: "Documentation réglementaire", total: docs.length, done: docs.filter((x) => x.ok).length, manquants: docs.filter((x) => !x.ok) },
    { id: "kyc", label: "KYC / LAB — entrée en mission", total: etapes.length, done: etapes.filter((x) => x.on).length, etapes },
    {
      id: "tasks",
      label: "Diligences administratives — entrée en mission",
      total: taches.length,
      done: taches.filter((x) => x.ok).length,
      manquants: taches.filter((x) => !x.ok),
      items: taches,
    },
  ];
  const total = sections.reduce((s, x) => s + x.total, 0);
  const done = sections.reduce((s, x) => s + x.done, 0);
  const overall = total ? Math.round((done / total) * 100) : 0;
  const nbOk = sections.filter((x) => x.done === x.total).length;
  const kycValide = etapes.find((e) => e.id === "validation").on;
  const statut = overall === 100 ? "ok" : !kycValide ? "alerte" : "encours";
  return { sections, overall, nbOk, statut };
}
